import React, { useEffect, useRef } from "react";
import { useRollbar } from "@rollbar/react";

import { useIncode } from "./IncodeContext";
import { incodeUiConfig } from "./incodeRequest";

const CAMERA_BLOCKED_ERROR_CODES = new Set([
  "PERMISSION_DENIED",
  "CAMERA_PERMISSION_DENIED",
  "WEBCAM_PERMISSION_DENIED",
  "CAMERA_NOT_AVAILABLE",
  "CAMERA_UNAVAILABLE",
]);

const normalizeErrorCode = (error: any): string => {
  const candidate =
    error?.code ??
    error?.errorCode ??
    error?.error?.code ??
    error?.error?.errorCode ??
    error?.name ??
    "";
  return candidate.toString().trim().toUpperCase().replace(/\s+/g, "_");
};

export const CaptureId = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const rendererRef = useRef<{ close?: () => void } | undefined>(undefined);
  const { incode, token, track, navigateTo } = useIncode();
  const rollbar = useRollbar();

  useEffect(() => {
    rendererRef.current = incode.renderCaptureId(containerRef.current, {
      session: { token },
      onSuccess: async () => {
        try {
          await incode.processId({ token });
          track("capture_id_success");
        } catch (error) {
          rollbar.error("Incode Process ID Error", error);
          track("capture_id_process_error");
        }
        navigateTo("capture_face");
      },
      onError: async (error: any) => {
        rollbar.error("Incode Render Capture ID Error", error);
        const code = normalizeErrorCode(error);

        if (CAMERA_BLOCKED_ERROR_CODES.has(code)) {
          track("capture_id_permissions_error");
          navigateTo("enable_camera_access");
          return;
        }

        try {
          await incode.processId({ token });
        } catch (processError) {
          rollbar.error("Incode Process ID Error (after capture error)", processError);
          track("capture_id_process_error");
        }

        if (code === "NO_MORE_TRIES" || code === "NO_MORE_ATTEMPTS") {
          track("capture_id_no_more_tries");
        } else {
          track("capture_id_error");
        }
        navigateTo("capture_face");
      },
      forceIdV2: true,
      throwOnCameraPermissionDenied: true,
      uiConfig: incodeUiConfig,
    });

    return () => {
      rendererRef.current?.close?.();
    };
  }, [incode, token, track, navigateTo, rollbar]);

  return <div ref={containerRef} />;
};
